import { useEffect, useState} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import Link from './Link';


import ActiveCollaboratorsChip from 'components/ActiveCollaboratorsChip';

import useUserIdentity from 'hooks/useUserIdentity';
import useProject from 'hooks/useProject';
import useCollabs from 'hooks/useCollabs';
import useFHRequest from 'hooks/useFHRequest';
import { CollabProps } from 'stores/collab_store';
import { FHRequestType } from 'stores/fhrequest_store';

import { WORK_ITEM_FONT_SIZE } from 'components/ListicleWorkItem';
import { PROJECT_HEADER_FONT_SIZE } from 'components/ListicleProject';
import { NewCollabButton } from 'components/ListicleProject';

const WorkItemChunk = observer(( { title, url, uuid }: { title: string, url: string, uuid: string } ) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [scrollY, setScrollY] = useState(0);
    const handleScroll = () => {
            setScrollY(document.body.scrollTop);
        };

    useEffect(() => {
        const body = document.body;
        body.addEventListener('scroll', handleScroll);
        return () => body.removeEventListener('scroll', handleScroll);
    }, []);
    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                minWidth: "0px",
            }}>
            <Typography
                noWrap
                sx={{
                    minWidth: "0px",
                    overflow: "hidden",
                    width: "100%",
                    textOverflow: "ellipsis",
                    fontSize: WORK_ITEM_FONT_SIZE,
                    mb: 0,
                    pb: 0,
                    maxWidth: 300,
                    cursor: "pointer"
                }}
                level="title-md"
                onClick={() => {
                    // if we're scrolled down, scroll to top
                    // othwerwise, redirect to '/collab/:collabKey'
                    if (scrollY > 0 && location.pathname.endsWith(uuid)) {
                        document.body.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth' });
                    } else {
                        navigate(url);
                    }}}
            >
                {title}
            </Typography>
        </Stack>
    );
});

export const CollabChunk = observer(( { collab }: { collab: CollabProps } ) => {
    if(!collab) {
        return null;
    }
    return <WorkItemChunk title={collab.title} uuid={collab.uuid} url={`/collab/${collab.uuid}`} />;
});

export const RequestChunk = observer(( { request }: { request: FHRequestType } ) => {
    if(!request) {
        return null;
    }
    return <WorkItemChunk title={request.title} uuid={request.uuid} url={`/request/${request.uuid}`} />;
});

const _ProjectHeader = observer(() => {
    const collabKey = useParams<{collabKey: string}>().collabKey;
    const requestKey = useParams<{requestKey: string}>().requestKey;
    const projectKey = useParams<{projectKey: string}>().projectKey;
    const { currentIdentity } = useUserIdentity();
    const { getCollab, fetchCollab, collabs } = useCollabs();
    const { getRequest, fetchRequest, items } = useFHRequest();
    // const [project, setProject] = useState<ProjectProps | undefined>(undefined);
    const [collab, setCollab] = useState<CollabProps | null>(null);
    const [request, setRequest] = useState<FHRequestType | null>(null);
    const { getProject } = useProject();

    const pk = projectKey || collab?.project || request?.project
    const project = pk ? getProject(pk) : null;



    useEffect(() => {
        if(!currentIdentity.uuid) return;
        if(collabKey) {
            const c = getCollab(collabKey);
            if(c)
                setCollab(c);
            else
                fetchCollab(currentIdentity.uuid, collabKey);
        }
        else
            setCollab(null);
    }, [currentIdentity.uuid, collabKey, collabs.length]);

    useEffect(() => {
        if(!currentIdentity.uuid) return;
        if(requestKey) {
            const c = getRequest(requestKey);
            if(c)
                setRequest(c);
            else
                fetchRequest(requestKey);
        }
        else
            setRequest(null);
    }, [currentIdentity.uuid, requestKey, items.length]);


    return (
        <Stack
            direction="row"
            alignItems="center"
            overflow="hidden"
            width="98%"
            height="85%"
            pr={1}
            justifyContent="space-between"
        >

            <Stack spacing={0}>

                {collab &&
                    <CollabChunk collab={collab} />}

                {request &&
                    <RequestChunk request={request} />}

                    { project &&
                <Link to={"/project/" + project.key}>
                    <Typography
                        sx={{ transition: 'all ease-in-out 0.1s' }}
                        level={collab || request ? "title-sm" : "title-lg"}
                        fontSize={collab || request ?
                            undefined :
                            PROJECT_HEADER_FONT_SIZE}
                        noWrap>{project.title}</Typography>
                        </Link> }
            </Stack>

            {collab?.uuid &&
                <ActiveCollaboratorsChip
                    size='natural'
                    associated_type="collab"
                    associated_uuid={collab.uuid} />
            }

            {project && !(collab?.uuid || request?.uuid)  &&
                <NewCollabButton project={project} />}

        </Stack>
    );
});

export default _ProjectHeader;
